import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import type { CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import type { CoverEditorDispatch } from '../doc/useCoverEditorDoc';
import { resizeFromCorner, resizeFromSide, rotationFromPointer, toBoxFrame, type StagePoint } from './boxGestures';
import { snapBox, type SnapBox, type SnapResult } from './snapGuides';

export type BoxGesture = 'move' | 'corner' | 'side' | 'rotate';

interface UseBoxPointerProps {
  /** The element of the cover square: the pointer is measured against it. */
  cover: RefObject<HTMLElement | null>;
  dispatch: CoverEditorDispatch;
  /** The size of a box as shares of the side (from the measure of the stage). */
  sizeOf: (box: CoverTextBox) => { width: number; height: number };
  /** The other boxes, for the snap lines. */
  othersOf: (id: string) => SnapBox[];
  isSnapOn: boolean;
  onGuides: (guides: Pick<SnapResult, 'vertical' | 'horizontal'> | null) => void;
  disabled: boolean;
}

interface ActiveGesture {
  gesture: BoxGesture;
  box: CoverTextBox;
  startPoint: StagePoint;
  startDistance: number;
  pointerId: number;
}

/**
 * The pointer gestures of a text box on the stage (design cover-editor b1, d4): drag to move (with
 * snap lines), a corner handle to size, a side handle to set the wrap width, the round handle to
 * turn. Every move of one gesture is one Undo step (`coalesce`). The pointer is captured, so a
 * drag goes on outside the cover.
 */
export function useBoxPointer(props: UseBoxPointerProps) {
  const { cover, dispatch, sizeOf, othersOf, isSnapOn, onGuides, disabled } = props;
  const active = useRef<ActiveGesture | null>(null);

  const pointOf = useCallback(
    (event: { clientX: number; clientY: number }): StagePoint | null => {
      const rect = cover.current?.getBoundingClientRect();
      if (rect === undefined || rect.width <= 0) {
        return null;
      }
      return { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height };
    },
    [cover]
  );

  const start = useCallback(
    (event: ReactPointerEvent<HTMLElement>, box: CoverTextBox, gesture: BoxGesture) => {
      const point = pointOf(event);
      if (disabled || point === null || event.button !== 0) {
        return;
      }
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      const local = toBoxFrame(point, box);
      active.current = {
        gesture,
        box,
        startPoint: point,
        startDistance: Math.hypot(local.x, local.y),
        pointerId: event.pointerId,
      };
      dispatch({ type: 'select', selection: { kind: 'text', id: box.id } });
    },
    [disabled, dispatch, pointOf]
  );

  const move = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const { current } = active;
      const point = pointOf(event);
      if (current === null || point === null || current.pointerId !== event.pointerId) {
        return;
      }
      const { box, gesture } = current;
      const coalesce = `text:${box.id}:${gesture}`;
      if (gesture === 'move') {
        const size = sizeOf(box);
        const moved = {
          x: box.x + point.x - current.startPoint.x,
          y: box.y + point.y - current.startPoint.y,
          ...size,
        };
        // Alt turns the snap off for this move, like a slide editor.
        const snapped = snapBox(moved, othersOf(box.id), isSnapOn && !event.altKey && box.rotationDeg === 0);
        onGuides(snapped.vertical.length + snapped.horizontal.length > 0 ? snapped : null);
        dispatch({ type: 'updateText', id: box.id, patch: { x: snapped.x, y: snapped.y }, coalesce });
        return;
      }
      if (gesture === 'rotate') {
        const rotationDeg = rotationFromPointer(point, box, event.shiftKey);
        dispatch({ type: 'updateText', id: box.id, patch: { rotationDeg }, coalesce });
        return;
      }
      const local = toBoxFrame(point, box);
      const patch =
        gesture === 'corner'
          ? resizeFromCorner(box, current.startDistance, Math.hypot(local.x, local.y))
          : resizeFromSide(local.x);
      dispatch({ type: 'updateText', id: box.id, patch, coalesce });
    },
    [dispatch, isSnapOn, onGuides, othersOf, pointOf, sizeOf]
  );

  const end = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (active.current === null || active.current.pointerId !== event.pointerId) {
        return;
      }
      active.current = null;
      onGuides(null);
      // The next gesture is a new Undo step.
      dispatch({ type: 'commit' });
    },
    [dispatch, onGuides]
  );

  return { start, move, end };
}
